// ProfitHarvest — the bridge that makes the two games one. After each weekly
// settle, the business profit (positive cashflow) is offered to the lifecycle:
//   (A) keep as cash  (B) put into savings (interest → passive income)
//   (C) invest in a new asset  → moves the Freedom Meter.
// Self-mounting modal: reads the weekly store for un-harvested settled weeks and
// the lifecycle store for where things stand.

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import type { Txn } from '@biz-kid/engine';
import { businessAssetReadiness, ASSET_STREAK_WEEKS } from '@biz-kid/engine';
import { useDispatch, useGameState } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip, money } from '../ui/kit.js';
import { CartoonMiko as Miko } from '../ui/cartoon.js';
import { Confetti } from '../ui/Confetti.js';
import { useModalSlot } from '../ui/modalQueue.js';
import { useLife, useLifeComputed, useLifeDispatch, newId } from './store.js';
import { useVoice } from '../voice/VoiceContext.js';

const SAVINGS_MONTHLY_RATE = 0.02; // gentle interest

type InvestOption = { type: string; name: string; icon: string; value: number; monthlyIncome: number; monthlyCost: number };
const INVEST_OPTIONS: InvestOption[] = [
  { type: 'business', name: 'Preset pack', icon: '🎨', value: 30, monthlyIncome: 5, monthlyCost: 0 },
  { type: 'business', name: 'Clip channel', icon: '🎬', value: 45, monthlyIncome: 7, monthlyCost: 0 },
  { type: 'stocks', name: 'Beat licence', icon: '🎧', value: 75, monthlyIncome: 10, monthlyCost: 0 },
  { type: 'vendingMachine', name: 'Course Creator', icon: '📚', value: 150, monthlyIncome: 22, monthlyCost: 2 },
];

/** Weekly cashflow = active + passive − expense − debt (capital excluded). */
function weekCashflow(ledger: Txn[], w: number): number {
  return ledger
    .filter((t) => t.week === w)
    .reduce((sum, t) => (t.category === 'capital' ? sum : sum + t.amount), 0);
}

export function ProfitHarvest() {
  const week = useGameState((s) => s.clock.week);
  const ledger = useGameState((s) => s.ledger);
  const businesses = useGameState((s) => s.businesses);
  const life = useLife();
  const computed = useLifeComputed();
  const dispatch = useLifeDispatch();
  const engineDispatch = useDispatch();
  const [mode, setMode] = useState<'choose' | 'invest' | 'registered'>('choose');

  // "Business as Asset" readiness — after 3 profitable weeks, offer to convert
  // the running business into a passive lifecycle asset. Pure engine helper.
  const bizId = businesses[0]?.id;
  const readiness = useMemo(() => {
    if (!bizId) return null;
    // The helper only reads clock.week, businesses, and ledger.
    return businessAssetReadiness(
      { clock: { week }, businesses, ledger } as unknown as Parameters<typeof businessAssetReadiness>[0],
      bizId,
    );
  }, [bizId, businesses, ledger, week]);

  const lastHarvested = life.lastHarvestedWeek ?? 0;
  const settledWeek = week - 1; // the most recent fully-settled week

  // Sum the positive profit of every settled week we haven't swept in yet.
  let pendingProfit = 0;
  for (let w = lastHarvested + 1; w <= settledWeek; w++) {
    pendingProfit += Math.max(0, weekCashflow(ledger, w));
  }
  const hasUnharvested = settledWeek > lastHarvested;
  const show = hasUnharvested && pendingProfit > 0;

  // If a week settled with no profit, advance the marker quietly (no modal).
  useEffect(() => {
    if (hasUnharvested && pendingProfit <= 0) {
      dispatch({ type: 'SET_HARVESTED_WEEK', week: settledWeek });
    }
  }, [hasUnharvested, pendingProfit, settledWeek, dispatch]);

  // Reset to the choose screen whenever a fresh harvest appears.
  useEffect(() => {
    if (show) setMode('choose');
  }, [show]);

  // Modal queue — Payday is priority 80. Wait if a higher-priority modal
  // (year flow, Vision celebration, Resilience) is currently showing.
  const { canShow } = useModalSlot('payday', show, 80);

  // Hold the card back ~1.2s after a fresh settle so the on-button coin burst
  // (WeekRunner) plays first — burst → then the Payday celebration.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!show) { setReady(false); return; }
    const t = setTimeout(() => setReady(true), 1150);
    return () => clearTimeout(t);
  }, [show]);

  // MIKO SPEAKS — announce the haul aloud once the Payday card lands. Keyed to the
  // settled week so it fires once per payday, not on every re-render.
  const { speak } = useVoice();
  const spokePaydayRef = useRef<number>(0);
  useEffect(() => {
    if (canShow && ready && mode === 'choose' && spokePaydayRef.current !== settledWeek) {
      spokePaydayRef.current = settledWeek;
      void speak(`Payday! You banked ${money(pendingProfit)} this week. Now make it work for you!`);
    }
  }, [canShow, ready, mode, settledWeek, pendingProfit, speak]);

  if (!canShow || !ready) return null;

  const close = () => dispatch({ type: 'SET_HARVESTED_WEEK', week: settledWeek });

  const keepCash = () => {
    dispatch({ type: 'ADJUST_CASH', delta: pendingProfit });
    close();
  };
  const save = () => {
    dispatch({ type: 'DEPOSIT_TO_SAVINGS', amount: pendingProfit, monthlyRate: SAVINGS_MONTHLY_RATE });
    close();
  };
  const invest = (opt: InvestOption) => {
    dispatch({ type: 'ADJUST_CASH', delta: pendingProfit });
    dispatch({
      type: 'BUY_ASSET',
      asset: { id: newId(opt.type), type: opt.type, name: opt.name, value: opt.value, monthlyIncome: opt.monthlyIncome, monthlyCost: opt.monthlyCost },
    });
    close();
  };

  // The Business-as-Asset moment. The sim keeps running; the lifecycle now also
  // gets a recurring monthly passive income. This week's profit is still kept
  // as cash (registering doesn't *replace* the week, it's added on top).
  const registerAsAsset = () => {
    if (!bizId || !readiness?.ready) return;
    const err = engineDispatch({ kind: 'RegisterBusinessAsAsset', businessId: bizId });
    if (err) {
      console.warn('RegisterBusinessAsAsset failed:', err);
      return;
    }
    dispatch({
      type: 'ADD_ASSET',
      asset: {
        id: `biz-${bizId}`,
        type: 'business',
        name: `${businesses[0]?.defId === 'smoothieStand' ? 'Drop Store' : 'Your business'} (passive)`,
        value: readiness.value,
        monthlyIncome: readiness.monthlyIncome,
        monthlyCost: 0,
      },
    });
    dispatch({ type: 'ADJUST_CASH', delta: pendingProfit });
    setMode('registered');
  };
  const finishRegistration = () => {
    close();
  };

  const spendable = pendingProfit + life.cash;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        display: 'grid',
        placeItems: 'center',
        padding: 18,
        background: 'rgba(10,14,26,0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <motion.div initial={{ y: 16, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }} style={{ width: '100%', maxWidth: 460 }}>
        <Card style={{ position: 'relative', overflow: 'hidden' }}>
          {mode === 'choose' && (
            <>
              <Confetti count={24} />
              <div style={{ position: 'relative', textAlign: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                  <Miko size={64} mood="cheering" />
                </div>
                <Eyebrow color="var(--up)">Payday — week {settledWeek}</Eyebrow>
                <motion.div
                  className="bk-tnum"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: [0.6, 1.15, 1], opacity: 1 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  style={{ fontSize: 46, fontWeight: 900, color: 'var(--up)', lineHeight: 1.05 }}
                >
                  +{money(pendingProfit)}
                </motion.div>
                <p style={{ margin: '6px 0 0', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                  &ldquo;Not bad. Now make it work for you.&rdquo;
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--text-3)', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  — Miko
                </p>
              </div>
            </>
          )}

          {/* Plain conditional rendering — AnimatePresence mode="wait" gets stuck on
              a 3-way switch with nested ternaries (previous scene never exits, next
              never mounts). Each motion.div animates itself in on first mount. */}
          {mode === 'choose' ? (
              <motion.div key="choose" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: 'grid', gap: 10, marginTop: 6 }}>
                {readiness?.ready && (
                  <Choice
                    icon="🎩"
                    title="GO PASSIVE"
                    sub={`${ASSET_STREAK_WEEKS} weeks up. Earns ${money(readiness.monthlyIncome)}/mo FOREVER.`}
                    onClick={registerAsAsset}
                    tone="brand"
                    arrow
                    pill="MONEY MACHINE"
                  />
                )}
                <Choice icon="💵" title="KEEP IT" sub="Cash in hand. Earns nothing." onClick={keepCash} />
                <Choice icon="🏦" title="STACK IT" sub={`Earns ~${Math.round(SAVINGS_MONTHLY_RATE * 100)}%/mo. Free money.`} onClick={save} tone="up" />
                <Choice icon="📈" title="BUY A MONEY MACHINE" sub="Pays you every single month." onClick={() => setMode('invest')} tone="brand" arrow />
                <div style={{ textAlign: 'center', marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 800 }}>FREEDOM LEVEL: </span>
                  <Chip tone={computed.freedomMode ? 'up' : 'brand'}>{computed.freedomPct}%</Chip>
                </div>
              </motion.div>
            ) : mode === 'registered' ? (
              <motion.div key="registered" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} style={{ display: 'grid', gap: 14, marginTop: 6, textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Miko size={84} mood="cheering" />
                </div>
                <Eyebrow color="var(--brand)">The Business as Asset moment</Eyebrow>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 600, lineHeight: 1.5, color: 'var(--text)' }}>
                  "You stopped working in your business. Now your business works for you."
                </p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-3)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  — Rich Dad
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: 12, borderRadius: 14, background: 'var(--brand-soft)', border: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 700 }}>Passive income</div>
                    <div className="bk-tnum" style={{ fontSize: 22, fontWeight: 900, color: 'var(--up)' }}>+{money(readiness?.monthlyIncome ?? 0)}/mo</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 700 }}>Balance-sheet value</div>
                    <div className="bk-tnum" style={{ fontSize: 22, fontWeight: 900, color: 'var(--brand)' }}>{money(readiness?.value ?? 0)}</div>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5, fontWeight: 700 }}>
                  Still runs. Now it PAYS YOU too. 🔥
                </p>
                <Button tone="primary" full onClick={finishRegistration}>LET’S GO →</Button>
              </motion.div>
            ) : (
              <motion.div key="invest" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ marginTop: 6 }}>
                <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, fontWeight: 700 }}>
                  YOU’VE GOT <b style={{ color: 'var(--up)' }}>{money(spendable)}</b>. Each one pays monthly:
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {INVEST_OPTIONS.map((opt) => {
                    const affordable = opt.value <= spendable;
                    return (
                      <button
                        key={opt.name}
                        type="button"
                        disabled={!affordable}
                        onClick={() => invest(opt)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          textAlign: 'left',
                          padding: 12,
                          borderRadius: 14,
                          border: '1px solid var(--border)',
                          background: affordable ? 'var(--surface-2)' : 'var(--surface)',
                          opacity: affordable ? 1 : 0.5,
                          cursor: affordable ? 'pointer' : 'not-allowed',
                          fontFamily: 'inherit',
                        }}
                      >
                        <span style={{ fontSize: 24 }}>{opt.icon}</span>
                        <span style={{ flex: 1 }}>
                          <span style={{ display: 'block', fontWeight: 800, fontSize: 14.5, color: 'var(--text)' }}>{opt.name}</span>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-3)' }}>costs {money(opt.value)}</span>
                        </span>
                        <span className="bk-tnum" style={{ fontWeight: 900, color: 'var(--up)', fontSize: 14 }}>+{money(opt.monthlyIncome)}/mo</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <Button tone="ghost" full onClick={() => setMode('choose')}>← BACK</Button>
                </div>
              </motion.div>
            )}
        </Card>
      </motion.div>
    </div>
  );
}

function Choice({ icon, title, sub, onClick, tone, arrow, pill }: { icon: string; title: string; sub: string; onClick: () => void; tone?: 'up' | 'brand'; arrow?: boolean; pill?: string }) {
  const accent = tone === 'up' ? 'var(--up)' : tone === 'brand' ? 'var(--brand)' : 'var(--border)';
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.99 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        textAlign: 'left',
        padding: 14,
        borderRadius: 14,
        border: `1px solid ${tone ? accent : 'var(--border)'}`,
        background: tone === 'up' ? 'var(--up-soft)' : tone === 'brand' ? 'var(--brand-soft)' : 'var(--surface-2)',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <span style={{ fontSize: 24 }}>{icon}</span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>{title}</span>
          {pill && (
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999, background: accent, color: 'var(--brand-ink)' }}>
              {pill}
            </span>
          )}
        </span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>{sub}</span>
      </span>
      {arrow && <span style={{ color: accent, fontWeight: 900 }}>→</span>}
    </motion.button>
  );
}
